import { Drawer, Grid, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type { CitationView } from "./types/education.types.ts"

const { Text } = Typography

interface SourcesPanelProps {
  open: boolean
  onClose: () => void
  citations: CitationView[]
}

const isSafeCitationUrl = (url: string | null): url is string =>
  !!url && /^https?:\/\//i.test(url)

/**
 * Screen 2's "Sources {n}" panel — split so a reader can tell a claim backed
 * by this specific lead's own facts (`kind === "lead"`) from one backed by
 * EZBuild's general approved knowledge (`kind === "source" | "general"`).
 * Opens in-place per the spec (§3, Screen 2 behavior) as a right-hand Drawer
 * on desktop, dropping to a bottom sheet below `md` (Global Constraints,
 * Responsive behavior — Sources).
 */
export function SourcesPanel({ open, onClose, citations }: SourcesPanelProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()

  const leadCitations = citations.filter((citation) => citation.kind === "lead")
  const generalCitations = citations.filter((citation) => citation.kind !== "lead")

  const renderCitation = (citation: CitationView) => (
    <li key={citation.id} data-testid={`source-${citation.id}`}>
      {isSafeCitationUrl(citation.url) ? (
        <a href={citation.url} target={"_blank"} rel={"noopener noreferrer"}>
          [{citation.number}] {citation.label}
        </a>
      ) : (
        <span>
          [{citation.number}] {citation.label}
        </span>
      )}
    </li>
  )

  return (
    <Drawer
      title={t("projectEducation.sourcesPanel.title")}
      open={open}
      onClose={onClose}
      placement={screens.md ? "right" : "bottom"}
      height={screens.md ? undefined : "70vh"}
      width={420}
      destroyOnHidden
    >
      {citations.length === 0 ? (
        <Text type={"secondary"}>{t("projectEducation.sourcesPanel.empty")}</Text>
      ) : (
        <>
          {leadCitations.length > 0 && (
            <div className={"mb-4"} data-testid={"sources-lead-details"}>
              <Text strong>{t("projectEducation.sourcesPanel.leadDetails")}</Text>
              <ul className={"mt-2 list-none space-y-1 pl-0 text-sm"}>
                {leadCitations.map(renderCitation)}
              </ul>
            </div>
          )}
          {generalCitations.length > 0 && (
            <div data-testid={"sources-general-guidance"}>
              <Text strong>{t("projectEducation.sourcesPanel.generalGuidance")}</Text>
              <ul className={"mt-2 list-none space-y-1 pl-0 text-sm"}>
                {generalCitations.map(renderCitation)}
              </ul>
            </div>
          )}
        </>
      )}
    </Drawer>
  )
}
